import {
  getRangeFilterDefinitions,
  getRangeFilterEndpoint,
  getRangeFilterMetadataKey,
  getRangeFilterMetadataQuery,
  sanitizeRangeFilterQuery,
  type RangeFilterDefinition,
  type RangeFilterMetadata,
} from '~/utils/rangeFilter';

export default defineNuxtRouteMiddleware(async (to) => {
  const endpoint = getRangeFilterEndpoint(to.path);
  if (!endpoint || !Object.keys(to.query).some((key) => key.startsWith('filter[') && key !== 'filter[search]')) return;

  // Price is always a range, so malformed price links need no metadata request.
  const priceResult = sanitizeRangeFilterQuery(to.query);
  if (priceResult.changed) {
    return navigateTo({ path: to.path, query: priceResult.query, hash: to.hash }, { replace: true, redirectCode: 302 });
  }

  const metadata = useState<RangeFilterMetadata | null>('catalog-range-filter-metadata', () => null);
  const key = getRangeFilterMetadataKey(to.path, to.query);
  const serverMetadata = import.meta.server ? useRequestEvent()?.context.catalogRangeFilterMetadata : undefined;

  if (serverMetadata?.key === key) metadata.value = serverMetadata;

  if (metadata.value?.key !== key) {
    // Keep only one page's definitions. Normal UI navigation reuses those registered by useCatalogFilters.
    const { data, error } = await useCustomFetch<{ data?: { filters?: RangeFilterDefinition[] } }>(
      endpoint,
      { query: getRangeFilterMetadataQuery(to.query), watch: false, timeout: 5000 },
      '2',
      true,
    );

    if (error.value && String(error.value.statusCode) !== '404') {
      return abortNavigation(createError({ statusCode: 503, statusMessage: 'Unable to load catalog filters' }));
    }

    if (!data.value?.data?.filters) return;

    metadata.value = { key, filters: getRangeFilterDefinitions(data.value.data.filters) };
  }

  const result = sanitizeRangeFilterQuery(to.query, metadata.value.filters);
  if (result.changed) {
    return navigateTo({ path: to.path, query: result.query, hash: to.hash }, { replace: true, redirectCode: 302 });
  }
});
